/**
 * Recipes Filter Drawer
 * Handles the filter drawer open/close, range slider, and filter application
 */
document.addEventListener("DOMContentLoaded", () => {
  const drawer = document.querySelector("[data-filter-drawer]");
  if (!drawer) return;

  const applyButton = drawer.querySelector("[data-filter-apply]");
  const countDisplay = drawer.querySelector("[data-filter-count]");
  const activeFacetsContainer = document.querySelector("[data-active-facets]");
  const setDrawerDirection = () => {
    const isBottom = window.matchMedia("(max-width: 999px)").matches;
    drawer.setAttribute("open-from", isBottom ? "bottom" : "left");
  };

  const rangeGroup = drawer.querySelector(".range-group");
  const rangeMin = drawer.querySelector("[data-range-min]");
  const rangeMax = drawer.querySelector("[data-range-max]");
  const timeMinInput = drawer.querySelector("[data-time-min-input]");
  const timeMaxInput = drawer.querySelector("[data-time-max-input]");

  const difficultyCheckboxes = drawer.querySelectorAll(
    'input[name="recipe-difficulty"]'
  );

  const maxTime = parseInt(drawer.dataset.maxTime, 10) || 120;

  let pendingFilters = {
    minTime: 0,
    maxTime: maxTime,
    difficulties: [],
  };

  let appliedFilters = {
    minTime: 0,
    maxTime: maxTime,
    difficulties: [],
  };

  // Get recipes data
  let allRecipesData = [];
  const dataScript = document.getElementById("recipes-data");
  if (dataScript) {
    try {
      const jsonText = dataScript.textContent.trim();
      allRecipesData = JSON.parse(jsonText).filter(
        (recipe) => recipe && recipe.name
      );
    } catch (e) {
      console.error("Error parsing recipes data:", e);
      allRecipesData = [];
    }
  }

  if (applyButton) {
    applyButton.addEventListener("click", applyFilters);
  }

  setDrawerDirection();
  window.addEventListener("resize", setDrawerDirection);
  drawer.addEventListener("dialog:after-show", handleDrawerOpen);

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && drawer.hasAttribute("open")) {
      if (typeof drawer.hide === "function") {
        drawer.hide();
      } else {
        drawer.removeAttribute("open");
      }
    }
  });

  initializeRangeSlider();
  initializeCheckboxes();
  updatePreviewCount();
  renderActiveFacets();

  function handleDrawerOpen() {
    pendingFilters = {
      ...appliedFilters,
      difficulties: [...appliedFilters.difficulties],
    };
    syncUIWithPendingFilters();
    updatePreviewCount();
  }

  if (activeFacetsContainer) {
    activeFacetsContainer.addEventListener("click", (event) => {
      const clearAllButton = event.target.closest("[data-remove-all]");
      if (clearAllButton) {
        resetFilters();
        return;
      }

      const removeButton = event.target.closest("[data-remove-facet]");
      if (!removeButton) return;

      removeFacet(removeButton.dataset.type, removeButton.dataset.value);
    });
  }

  function syncUIWithPendingFilters() {
    if (rangeMin) rangeMin.value = pendingFilters.minTime;
    if (rangeMax) rangeMax.value = pendingFilters.maxTime;
    if (timeMinInput) timeMinInput.value = pendingFilters.minTime;
    if (timeMaxInput) timeMaxInput.value = pendingFilters.maxTime;

    updateRangeFill();

    // Sync checkboxes
    difficultyCheckboxes.forEach((checkbox) => {
      checkbox.checked = pendingFilters.difficulties.includes(checkbox.value);
    });
  }

  /**
   * Range Slider Logic
   */
  function initializeRangeSlider() {
    if (!rangeMin || !rangeMax) return;

    // Range slider events
    rangeMin.addEventListener("input", handleMinRangeChange);
    rangeMax.addEventListener("input", handleMaxRangeChange);

    // Input field events
    if (timeMinInput) {
      timeMinInput.addEventListener("input", handleMinInputChange);
      timeMinInput.addEventListener("blur", handleMinInputBlur);
    }
    if (timeMaxInput) {
      timeMaxInput.addEventListener("input", handleMaxInputChange);
      timeMaxInput.addEventListener("blur", handleMaxInputBlur);
    }

    updateRangeFill();
  }

  function handleMinRangeChange() {
    let minVal = parseInt(rangeMin.value, 10);
    let maxVal = parseInt(rangeMax.value, 10);

    // Prevent min from exceeding max
    if (minVal > maxVal) {
      minVal = maxVal;
      rangeMin.value = minVal;
    }

    pendingFilters.minTime = minVal;
    if (timeMinInput) timeMinInput.value = minVal;
    updateRangeFill();
    updatePreviewCount();
  }

  function handleMaxRangeChange() {
    let minVal = parseInt(rangeMin.value, 10);
    let maxVal = parseInt(rangeMax.value, 10);

    // Prevent max from going below min
    if (maxVal < minVal) {
      maxVal = minVal;
      rangeMax.value = maxVal;
    }

    pendingFilters.maxTime = maxVal;
    if (timeMaxInput) timeMaxInput.value = maxVal;
    updateRangeFill();
    updatePreviewCount();
  }

  function handleMinInputChange() {
    let val = parseInt(timeMinInput.value, 10) || 0;
    if (val < 0) val = 0;
    if (val > maxTime) val = maxTime;

    pendingFilters.minTime = val;
    if (rangeMin) rangeMin.value = val;
    updateRangeFill();
    updatePreviewCount();
  }

  function handleMinInputBlur() {
    let val = parseInt(timeMinInput.value, 10) || 0;
    if (val < 0) val = 0;
    if (val > pendingFilters.maxTime) val = pendingFilters.maxTime;

    pendingFilters.minTime = val;
    timeMinInput.value = val;
    if (rangeMin) rangeMin.value = val;
    updateRangeFill();
    updatePreviewCount();
  }

  function handleMaxInputChange() {
    let val = parseInt(timeMaxInput.value, 10) || maxTime;
    if (val < 0) val = 0;
    if (val > maxTime) val = maxTime;

    pendingFilters.maxTime = val;
    if (rangeMax) rangeMax.value = val;
    updateRangeFill();
    updatePreviewCount();
  }

  function handleMaxInputBlur() {
    let val = parseInt(timeMaxInput.value, 10) || maxTime;
    if (val < 0) val = 0;
    if (val > maxTime) val = maxTime;
    if (val < pendingFilters.minTime) val = pendingFilters.minTime;

    pendingFilters.maxTime = val;
    timeMaxInput.value = val;
    if (rangeMax) rangeMax.value = val;
    updateRangeFill();
    updatePreviewCount();
  }

  function updateRangeFill() {
    if (!rangeGroup || !rangeMin || !rangeMax) return;

    const minPercent = (parseInt(rangeMin.value, 10) / maxTime) * 100;
    const maxPercent = (parseInt(rangeMax.value, 10) / maxTime) * 100;

    // Update theme's CSS variables for range-group styling
    rangeGroup.style.setProperty("--range-min", minPercent + "%");
    rangeGroup.style.setProperty("--range-max", maxPercent + "%");
  }

  /**
   * Checkbox Logic
   */
  function initializeCheckboxes() {
    difficultyCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", handleCheckboxChange);
    });
  }

  function handleCheckboxChange(e) {
    const value = e.target.value;
    const checked = e.target.checked;

    if (checked) {
      if (!pendingFilters.difficulties.includes(value)) {
        pendingFilters.difficulties.push(value);
      }
    } else {
      pendingFilters.difficulties = pendingFilters.difficulties.filter(
        (d) => d !== value
      );
    }

    updatePreviewCount();
  }

  /**
   * Preview Count Calculation
   */
  function updatePreviewCount() {
    const matchingRecipes = getFilteredRecipes(pendingFilters);
    if (countDisplay) {
      countDisplay.textContent = matchingRecipes.length;
    }
  }

  function getFilteredRecipes(filters) {
    // First, get the current category filter from bubbles (if any)
    const urlParams = new URLSearchParams(window.location.search);
    const currentCategory = urlParams.get("filter") || "all";

    let recipes = [...allRecipesData];

    // Apply category filter (from bubbles)
    if (currentCategory !== "all") {
      const normalizedCategory = normalizeText(currentCategory);
      recipes = recipes.filter((recipe) => {
        const recipeCat = recipe.kategorie;
        if (Array.isArray(recipeCat)) {
          return recipeCat.some(
            (cat) => normalizeText(cat) === normalizedCategory
          );
        } else if (recipeCat) {
          return normalizeText(recipeCat) === normalizedCategory;
        }
        return false;
      });
    }

    // Apply time filter
    recipes = recipes.filter((recipe) => {
      const time = parseInt(recipe.totalTime, 10) || 0;
      return time >= filters.minTime && time <= filters.maxTime;
    });

    // Apply difficulty filter (only if at least one is selected)
    if (filters.difficulties.length > 0) {
      recipes = recipes.filter((recipe) => {
        const difficulty = recipe.recipe_difficulty || "";
        return filters.difficulties.includes(difficulty);
      });
    }

    return recipes;
  }

  // Helper to normalize text for comparison
  function normalizeText(text) {
    if (!text) return "";
    return text
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  }

  /**
   * Apply Filters
   */
  function applyFilters() {
    appliedFilters = {
      ...pendingFilters,
      difficulties: [...pendingFilters.difficulties],
    };

    emitFiltersApplied();

    if (typeof drawer.hide === "function") {
      drawer.hide();
    } else {
      drawer.removeAttribute("open");
    }
  }

  function removeFacet(type, value) {
    if (type === "time") {
      appliedFilters.minTime = 0;
      appliedFilters.maxTime = maxTime;
    } else if (type === "difficulty") {
      appliedFilters.difficulties = appliedFilters.difficulties.filter(
        (d) => d !== value
      );
    }

    pendingFilters = {
      ...appliedFilters,
      difficulties: [...appliedFilters.difficulties],
    };

    syncUIWithPendingFilters();
    updatePreviewCount();
    emitFiltersApplied();
  }

  function resetFilters() {
    appliedFilters = { minTime: 0, maxTime: maxTime, difficulties: [] };
    pendingFilters = { ...appliedFilters, difficulties: [] };
    syncUIWithPendingFilters();
    updatePreviewCount();
    emitFiltersApplied();
  }

  function emitFiltersApplied() {
    renderActiveFacets();

    const event = new CustomEvent("recipesFilterApplied", {
      detail: {
        minTime: appliedFilters.minTime,
        maxTime: appliedFilters.maxTime,
        difficulties: appliedFilters.difficulties,
      },
    });
    document.dispatchEvent(event);
  }

  function renderActiveFacets() {
    if (!activeFacetsContainer) return;

    const facets = [];

    if (appliedFilters.minTime > 0 || appliedFilters.maxTime < maxTime) {
      facets.push({
        type: "time",
        value: "time",
        label: `${appliedFilters.minTime} min - ${appliedFilters.maxTime} min`,
      });
    }

    appliedFilters.difficulties.forEach((difficulty) => {
      facets.push({
        type: "difficulty",
        value: difficulty,
        label: difficulty,
      });
    });

    if (!facets.length) {
      activeFacetsContainer.innerHTML = "";
      activeFacetsContainer.hidden = true;
      return;
    }

    const removeLabel = (
      activeFacetsContainer.dataset.removeLabel || "Remove filter"
    ).trim();
    const clearLabel =
      activeFacetsContainer.dataset.clearLabel || "Clear all";
    const safeRemoveLabel = escapeHtml(removeLabel);

    const removeIcon = `
      <svg role="presentation" stroke-width="2" focusable="false" width="10" height="10" class="icon icon-delete" viewBox="0 0 10 10">
        <path d="m.757.757 8.486 8.486m-8.486 0L9.243.757" stroke="currentColor"></path>
      </svg>
    `;

    const facetsHtml = facets
      .map((facet) => {
        const safeLabel = escapeHtml(facet.label);
        return `
          <div class="removable-facet">
            ${safeLabel}
            <button
              type="button"
              class="tap-area"
              data-remove-facet
              data-type="${facet.type}"
              data-value="${escapeHtml(facet.value || "")}"
              aria-label="${safeRemoveLabel} ${safeLabel}"
            >
              ${removeIcon}
            </button>
          </div>
        `;
      })
      .join("");

    const clearHtml =
      facets.length > 1
        ? `<button type="button" class="facet-clear-all text-subdued" data-remove-all>${escapeHtml(clearLabel)}</button>`
        : "";

    activeFacetsContainer.innerHTML = facetsHtml + clearHtml;
    activeFacetsContainer.hidden = false;
  }

  // Expose the filter function globally for recipes-bubbles.js integration
  window.recipesDrawerFilter = {
    getAppliedFilters: () => ({ ...appliedFilters }),
    applyToRecipes: (recipes) => {
      let filtered = [...recipes];

      // Apply time filter
      filtered = filtered.filter((recipe) => {
        const time = parseInt(recipe.totalTime, 10) || 0;
        return time >= appliedFilters.minTime && time <= appliedFilters.maxTime;
      });

      // Apply difficulty filter
      if (appliedFilters.difficulties.length > 0) {
        filtered = filtered.filter((recipe) => {
          const difficulty = recipe.recipe_difficulty || "";
          return appliedFilters.difficulties.includes(difficulty);
        });
      }

      return filtered;
    },
    hasActiveFilters: () => {
      return (
        appliedFilters.minTime > 0 ||
        appliedFilters.maxTime < maxTime ||
        appliedFilters.difficulties.length > 0
      );
    },
  };

  function escapeHtml(text) {
    if (!text) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
});
